import { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Upload,
  X,
  Video,
  Loader2,
  RefreshCw,
  Shield,
} from 'lucide-react';
import { useApiUrl } from '@refinedev/core';
import { uploadFile } from '@/lib/upload';
import type { VideoProcessingStatus } from '@/resources/modules/types';
import { YandexDiskImport } from './YandexDiskImport';
import { GoogleDriveImport } from './GoogleDriveImport';
import { VideoStateTimeline, type VideoStateSnapshot } from './VideoStateTimeline';

interface VideoUploadProps {
  /** URL загруженного видео (путь на сервере или fileId-based URL) */
  value: string;
  onChange: (url: string) => void;
  /** ID элемента модуля (для запуска обработки) */
  moduleItemId?: string;
  /** Статус обработки видео */
  processingStatus?: VideoProcessingStatus | null;
  /** Ошибка обработки */
  processingError?: string | null;
  /** URL HLS плейлиста (если обработка завершена) */
  hlsPlaylistUrl?: string | null;
  /** Защищено ли видео */
  isProtected?: boolean;
  /** Callback при изменении статуса обработки */
  onProcessingStatusChange?: () => void;
  className?: string;
  disabled?: boolean;
  maxSizeMB?: number;
}

interface UploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
}

const initialUploadState: UploadState = {
  isUploading: false,
  progress: 0,
  error: null,
};

const UPLOAD_TAB_STORAGE_KEY = 'video-upload-tab';

/**
 * Компонент загрузки видео с отображением статуса обработки.
 *
 * Lifecycle:
 * 1. Пользователь загружает видео файл → файл сохраняется как PRIVATE через /files/admin/upload
 * 2. URL файла записывается в moduleItem.videoUrl
 * 3. После сохранения урока → запускается обработка (process video)
 * 4. Статус обработки: PENDING → PROCESSING → READY / ERROR
 * 5. При READY: видео доступно через HLS (hlsPlaylistUrl)
 */
export function VideoUpload({
  value,
  onChange,
  moduleItemId,
  processingStatus,
  processingError: _processingError,
  hlsPlaylistUrl: _hlsPlaylistUrl,
  isProtected: _isProtected,
  onProcessingStatusChange,
  className,
  disabled = false,
  maxSizeMB = 20480,
}: VideoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>(initialUploadState);
  const [isProcessing, setIsProcessing] = useState(false);
  // Оптимистичный статус — ставится сразу при запуске обработки,
  // чтобы UI мгновенно отреагировал до прихода обновлённых пропсов
  const [optimisticStatus, setOptimisticStatus] = useState<VideoProcessingStatus | null>(null);
  // Свежий snapshot от VideoStateTimeline — решает, показывать ли табы выбора
  // источника или скрыть их (пока идёт импорт/обработка).
  const [liveSnapshot, setLiveSnapshot] = useState<VideoStateSnapshot | null>(null);
  // Какая вкладка загрузки активна. Сохраняется в localStorage, чтобы при
  // создании новых уроков не переключать вручную каждый раз.
  const [uploadTab, setUploadTab] = useState<'file' | 'yandex' | 'gdrive'>(() => {
    try {
      const stored = localStorage.getItem(UPLOAD_TAB_STORAGE_KEY);
      if (stored === 'yandex') return 'yandex';
      if (stored === 'gdrive') return 'gdrive';
      return 'file';
    } catch {
      return 'file';
    }
  });

  // Когда пропсы processingStatus обновятся — сбросить оптимистичный статус
  useEffect(() => {
    if (processingStatus) {
      setOptimisticStatus(null);
    }
  }, [processingStatus]);

  const effectiveStatus = processingStatus ?? optimisticStatus;
  const apiUrl = useApiUrl();

  // Polling статуса обработки теперь живёт в VideoStateTimeline — единственный
  // источник правды о фазах (import / hls / poster). VideoUpload получает
  // snapshot через onSnapshot и использует его только для решения о том,
  // показывать ли табы выбора источника.

  // Определяем «есть ли активная фаза» по live snapshot, иначе fallback на
  // переданный родителем processingStatus / optimisticStatus.
  const importPhase = liveSnapshot?.phases.find((p) => p.key === 'import') ?? null;
  const hlsPhase = liveSnapshot?.phases.find((p) => p.key === 'hls') ?? null;
  const anyActive =
    liveSnapshot?.anyActive === true ||
    effectiveStatus === 'PENDING' ||
    effectiveStatus === 'PROCESSING' ||
    (importPhase?.status === 'queued' || importPhase?.status === 'running') ||
    (hlsPhase?.status === 'queued' || hlsPhase?.status === 'running');
  const snapshotVideoUrl = liveSnapshot?.videoUrl;
  const hasVideo =
    value.length > 0 ||
    (typeof snapshotVideoUrl === 'string' && snapshotVideoUrl.length > 0);

  const uploadVideoFile = useCallback(
    async (file: File): Promise<string> => {
      const result = await uploadFile({
        file,
        access: 'PRIVATE',
        onProgress: ({ percent }) => {
          setUploadState((prev) => ({ ...prev, progress: percent }));
        },
      });
      return result.url;
    },
    []
  );

  const startProcessing = useCallback(async (videoUrl: string) => {
    if (!moduleItemId) {return;}

    setIsProcessing(true);
    try {
      const response = await fetch(`${apiUrl}/admin/video/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ moduleItemId: parseInt(moduleItemId, 10), videoUrl }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.errors?.[0] ?? 'Ошибка запуска обработки');
      }

      // Мгновенно показать статус "В очереди" до обновления пропсов
      setOptimisticStatus('PENDING');
      // Refresh parent to pick up new status
      onProcessingStatusChange?.();
    } catch (err) {
      setUploadState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Ошибка запуска обработки',
      }));
    } finally {
      setIsProcessing(false);
    }
  }, [moduleItemId, apiUrl, onProcessingStatusChange]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('video/')) {
        setUploadState((prev) => ({
          ...prev,
          error: 'Пожалуйста, выберите видео файл',
        }));
        return;
      }

      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        setUploadState((prev) => ({
          ...prev,
          error: `Размер файла не должен превышать ${maxSizeMB} МБ`,
        }));
        return;
      }

      setUploadState({ isUploading: true, progress: 0, error: null });

      try {
        const url = await uploadVideoFile(file);
        setUploadState({ isUploading: false, progress: 100, error: null });
        setFileName(file.name);
        onChange(url);
        // Автоматически запустить HLS-обработку после загрузки
        if (moduleItemId) {
          await startProcessing(url);
        }
      } catch (err) {
        setUploadState({
          isUploading: false,
          progress: 0,
          error: err instanceof Error ? err.message : 'Ошибка загрузки',
        });
      }
    },
    [onChange, uploadVideoFile, maxSizeMB, moduleItemId, startProcessing]
  );

  const handleReprocess = useCallback(async () => {
    if (!moduleItemId) {return;}

    setIsProcessing(true);
    try {
      const response = await fetch(`${apiUrl}/admin/video/${moduleItemId}/reprocess`, {
        method: 'POST',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.errors?.[0] ?? 'Ошибка перезапуска обработки');
      }

      onProcessingStatusChange?.();
    } catch (err) {
      setUploadState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Ошибка перезапуска',
      }));
    } finally {
      setIsProcessing(false);
    }
  }, [moduleItemId, apiUrl, onProcessingStatusChange]);

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {setIsDragging(true);}
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {setIsDragging(true);}
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) {return;}
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [disabled, handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileSelect(files[0]);
      }
      e.target.value = '';
    },
    [handleFileSelect]
  );

  const handleClick = useCallback(() => {
    if (!disabled && !uploadState.isUploading) {
      inputRef.current?.click();
    }
  }, [disabled, uploadState.isUploading]);

  const handleRemove = useCallback(() => {
    onChange('');
    setFileName(null);
    setUploadState(initialUploadState);
    setOptimisticStatus(null);
  }, [onChange]);

  // ── Когда показывать таб-блок выбора источника ──
  // Условие: видео ещё не загружено И нет активной фазы импорта/HLS.
  // Если admin отменил импорт — `hasVideo=false` и `anyActive=false`, табы вернутся.
  const showSourceTabs = !hasVideo && !anyActive;

  return (
    <div className={cn('space-y-3', className)}>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Live-таймлайн фаз — единственный источник правды о состоянии видео.
         Показываем при любом moduleItemId. */}
      {moduleItemId !== undefined && moduleItemId.length > 0 && (
        <VideoStateTimeline
          moduleItemId={moduleItemId}
          onSnapshot={(snap) => {
            setLiveSnapshot(snap);
            // Если HLS перешёл в success — обновим родителя чтобы он подхватил
            // hlsPlaylistUrl, isProtected и т.д. (тот же эффект что был раньше
            // у onProcessingStatusChange).
          }}
          onCancelled={() => {
            // После отмены — сбрасываем оптимистичный статус и просим родителя
            // перезапросить данные урока.
            setOptimisticStatus(null);
            onChange('');
            setFileName(null);
            onProcessingStatusChange?.();
          }}
          onReady={() => {
            onProcessingStatusChange?.();
          }}
        />
      )}

      {/* Прогресс client-side загрузки большого файла с компьютера — он не
         виден таймлайну, потому что job создаётся только после успешного
         завершения upload'а. Показываем рядом с таймлайном. */}
      {uploadState.isUploading && (
        <div className="space-y-1.5 rounded-md border p-2.5 bg-muted/20">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm">Загрузка с компьютера…</span>
            <span className="text-xs text-muted-foreground ml-auto">{uploadState.progress}%</span>
          </div>
          <Progress value={uploadState.progress} className="h-1.5" />
        </div>
      )}

      {/* Шапка с информацией об уже загруженном видео и кнопками заменить/удалить.
         Показываем только когда видео есть И сейчас ничего не происходит. */}
      {hasVideo && !anyActive && (
        <div className="rounded-lg border bg-muted/20 p-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Video className="h-5 w-5 text-muted-foreground shrink-0" />
            <span className="text-sm truncate text-muted-foreground" title={value}>
              {fileName ?? 'Видео загружено'}
            </span>
          </div>
          {!disabled && (
            <div className="flex items-center gap-1 shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClick}
                disabled={uploadState.isUploading}
              >
                <Upload className="h-3.5 w-3.5 mr-1" />
                Заменить
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleRemove}
                disabled={uploadState.isUploading}
              >
                <X className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Кнопки восстановления — если HLS упал в ERROR или ещё не запущен на готовом видео. */}
      {hasVideo && !anyActive && effectiveStatus === 'ERROR' && moduleItemId !== undefined && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleReprocess}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
          )}
          Повторить HLS-обработку
        </Button>
      )}
      {hasVideo && !anyActive && !effectiveStatus && moduleItemId !== undefined && value.length > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => startProcessing(value)}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Shield className="h-3.5 w-3.5 mr-1.5" />
          )}
          Запустить HLS-обработку
        </Button>
      )}

      {showSourceTabs ? (
        /* Вкладки: загрузка с компьютера или импорт с Яндекс.Диска.
           Последний выбор админа запоминаем в localStorage, чтобы при создании
           нового урока не приходилось каждый раз переключаться. */
        <Tabs
          value={uploadTab}
          onValueChange={(next) => {
            if (next === 'file' || next === 'yandex' || next === 'gdrive') {
              setUploadTab(next);
              try {
                localStorage.setItem(UPLOAD_TAB_STORAGE_KEY, next);
              } catch {
                // ignore — приватные режимы браузера / квота
              }
            }
          }}
          className="w-full"
        >
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="file" disabled={disabled || uploadState.isUploading}>
              С компьютера
            </TabsTrigger>
            <TabsTrigger value="yandex" disabled={disabled || uploadState.isUploading}>
              Яндекс.Диск
            </TabsTrigger>
            <TabsTrigger value="gdrive" disabled={disabled || uploadState.isUploading}>
              Google Drive
            </TabsTrigger>
          </TabsList>

          <TabsContent value="file" className="mt-3">
            <div
              onClick={handleClick}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={cn(
                'relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                isDragging && 'border-primary bg-primary/5',
                !isDragging && 'border-muted-foreground/25 hover:border-primary/50',
                disabled && 'opacity-50 cursor-not-allowed',
                uploadState.error && 'border-destructive'
              )}
            >
              {uploadState.isUploading ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-10 w-10 text-muted-foreground animate-spin" />
                  <div className="w-full max-w-xs">
                    <Progress value={uploadState.progress} className="h-2" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Загрузка видео... {uploadState.progress}%
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Video className="h-10 w-10 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Перетащите видео или нажмите для выбора
                  </p>
                  <p className="text-xs text-muted-foreground/70">
                    MP4, MOV, AVI, MKV, WebM. Максимум {maxSizeMB >= 1024 ? `${(maxSizeMB / 1024).toFixed(0)} ГБ` : `${maxSizeMB} МБ`}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="yandex" className="mt-3">
            <YandexDiskImport
              moduleItemId={moduleItemId}
              disabled={disabled || uploadState.isUploading}
              onImportStarted={onProcessingStatusChange}
            />
          </TabsContent>

          <TabsContent value="gdrive" className="mt-3">
            <GoogleDriveImport
              moduleItemId={moduleItemId}
              disabled={disabled || uploadState.isUploading}
              onImportStarted={onProcessingStatusChange}
            />
          </TabsContent>
        </Tabs>
      ) : null}

      {uploadState.error && (
        <p className="text-sm text-destructive">{uploadState.error}</p>
      )}
    </div>
  );
}
